// Enough Markdown for a skill's files (D62) and an agent's replies: YAML front matter (when
// asked for), headings, paragraphs with their line breaks, nested lists, quotes, fenced code,
// tables and rules, with inline code, bold, italics, links and bare or `<…>` URLs. Parsed into plain data that
// `Markdown` renders as React elements, so no text is ever injected as HTML.

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "link"; text: string; href: string };

export interface ListItem {
  text: Inline[];
  /** A list indented under this item. */
  children?: Extract<Block, { kind: "list" }>;
}

export type Block =
  | { kind: "front"; entries: [string, string][] }
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; text: Inline[] }
  | { kind: "paragraph"; text: Inline[] }
  | { kind: "list"; ordered: boolean; items: ListItem[] }
  | { kind: "quote"; text: Inline[] }
  | { kind: "code"; lang: string; text: string }
  | { kind: "table"; head: Inline[][]; rows: Inline[][][] }
  | { kind: "rule" };

// Bare URLs come before italics so an underscore inside one is not read as emphasis; a link or
// inline code that starts earlier still wins (the leftmost match is taken).
const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*|__[^_]+__)|(<https?:\/\/[^\s<>]+>)|(https?:\/\/[^\s<>`]+)|(\*[^*\s][^*]*\*|_[^_\s][^_]*_)|(\[[^\]]+\]\([^)\s]+\))/g;

/** Links a reader may follow: web pages and email, never `javascript:` or `data:`. */
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

/** A bare URL without the sentence around it: trailing punctuation and an unbalanced `)` are
 *  left as text (`(see https://x/a_(b))` keeps `(b)`; `…?expand=1.` drops the full stop). */
export function trimUrl(token: string): { url: string; rest: string } {
  let url = token;
  for (;;) {
    const last = url.at(-1) ?? "";
    if (".,;:!?'\"".includes(last)) url = url.slice(0, -1);
    else if (last === ")" && url.split("(").length < url.split(")").length) url = url.slice(0, -1);
    else break;
  }
  return { url, rest: token.slice(url.length) };
}

export function inline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index;
    if (at > last) out.push({ kind: "text", text: text.slice(last, at) });
    const token = m[0];
    if (m[1]) out.push({ kind: "code", text: token.slice(1, -1) });
    else if (m[2]) out.push({ kind: "strong", text: token.slice(2, -2) });
    else if (m[3]) out.push({ kind: "link", text: token.slice(1, -1), href: token.slice(1, -1) });
    else if (m[4]) {
      const { url, rest } = trimUrl(token);
      out.push({ kind: "link", text: url, href: url });
      if (rest) out.push({ kind: "text", text: rest });
    } else if (m[5]) out.push({ kind: "em", text: token.slice(1, -1) });
    else {
      const close = token.indexOf("](");
      const href = token.slice(close + 2, -1);
      const label = token.slice(1, close);
      out.push(SAFE_HREF.test(href) ? { kind: "link", text: label, href } : { kind: "text", text: label });
    }
    last = at + token.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

function frontMatter(lines: string[]): { block?: Block; rest: string[] } {
  if (lines[0]?.trim() !== "---") return { rest: lines };
  const end = lines.findIndex((l, i) => i > 0 && l.trim() === "---");
  if (end < 0) return { rest: lines };
  const entries = lines.slice(1, end).flatMap((l): [string, string][] => {
    const colon = l.indexOf(":");
    return colon > 0 ? [[l.slice(0, colon).trim(), l.slice(colon + 1).trim()]] : [];
  });
  return { block: { kind: "front", entries }, rest: lines.slice(end + 1) };
}

const LIST_ITEM = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/;
const TABLE_RULE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const indentOf = (s: string): number => s.replace(/\t/g, "    ").length;

/** Lines from `start` that form one list at `indent`, items nesting deeper ones. */
function list(lines: string[], start: number, indent: number): { block: Extract<Block, { kind: "list" }>; next: number } {
  const ordered = LIST_ITEM.exec(lines[start] ?? "")?.[3] !== undefined;
  const items: ListItem[] = [];
  let i = start;
  while (i < lines.length) {
    const m = LIST_ITEM.exec(lines[i] ?? "");
    if (!m) break;
    const depth = indentOf(m[1] ?? "");
    if (depth < indent) break;
    if (depth > indent) {
      const last = items.at(-1);
      if (!last) break;
      const nested = list(lines, i, depth);
      last.children = nested.block;
      i = nested.next;
      continue;
    }
    if ((m[3] !== undefined) !== ordered) break;
    items.push({ text: inline(m[4] ?? "") });
    i++;
  }
  return { block: { kind: "list", ordered, items }, next: i };
}

const cells = (row: string): Inline[][] =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => inline(c.trim()));

const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const startsBlock = (l: string): boolean => /^(#{1,6}\s|```)/.test(l) || LIST_ITEM.test(l) || RULE.test(l);

/** `frontMatter`: read a leading `---` block as YAML entries (a skill's SKILL.md). */
export function parseMarkdown(source: string, { frontMatter: front = false } = {}): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const { block, rest } = front ? frontMatter(lines) : { rest: lines };
  const blocks: Block[] = block ? [block] : [];
  let i = 0;
  while (i < rest.length) {
    const line = rest[i] ?? "";
    const fence = /^```(.*)$/.exec(line.trim());
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < rest.length && !(rest[i] ?? "").trim().startsWith("```")) body.push(rest[i++] ?? "");
      i++;
      blocks.push({ kind: "code", lang: (fence[1] ?? "").trim(), text: body.join("\n") });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = (heading[1] ?? "#").length as 1 | 2 | 3 | 4 | 5 | 6;
      blocks.push({ kind: "heading", level, text: inline((heading[2] ?? "").trim()) });
      i++;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      const parsed = list(rest, i, indentOf(item[1] ?? ""));
      blocks.push(parsed.block);
      i = parsed.next;
      continue;
    }
    if (line.includes("|") && TABLE_RULE.test(rest[i + 1] ?? "")) {
      const rows: Inline[][][] = [];
      i += 2;
      while (i < rest.length && (rest[i] ?? "").includes("|") && (rest[i] ?? "").trim() !== "") rows.push(cells(rest[i++] ?? ""));
      blocks.push({ kind: "table", head: cells(line), rows });
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const quote = line.startsWith(">");
    const para: string[] = [];
    while (i < rest.length) {
      const l = rest[i] ?? "";
      if (l.trim() === "" || startsBlock(l) || l.startsWith(">") !== quote) break;
      para.push(quote ? l.replace(/^>\s?/, "") : l.trim());
      i++;
    }
    // Line breaks inside a paragraph are kept: agents write "Done.\nChanged: …" and mean it.
    blocks.push({ kind: quote ? "quote" : "paragraph", text: inline(para.join("\n")) });
  }
  return blocks;
}
